import { env } from 'env';

export type MytaMarket = 'od' | 'spot';

export interface IMytaLease {
  leaseId: string;
  nodeId: string;
  slotIndex: number | string;
  streamerId: string;
  signallingUrl: string;
  playerUrl?: string;
  buildUri?: string;
  expiresAt?: string;
  ttlSec?: number;
  // Served by the myta-streams lease API from the slot row. `sessionReady` means the slot is ALREADY in the
  // venue — UE announced CLIENT_READY once, before this page existed, and will not announce it again, so a
  // re-attaching client must settle its own entry loader instead of waiting for that message.
  mapName?: string;
  sessionReady?: boolean;
  // The seat survived the disconnect but the UE on it did not belong to this page — it still held the
  // websocket session of the page that went away, and nothing can move it there. The server keeps the seat
  // and restarts that UE so this page's INIT starts a real session; until the streamer is back there is no
  // stream to wait for, so the entry watchdog must not reclaim the slot.
  restarting?: boolean;
  etaSec?: number;
  // Which market the GPU box behind this seat was bought on (myta-streams 2026-09-11). `spot` is the cheap,
  // interruptible one: slower to get (the D-49 pass buys spot before waking any on-demand box, and a spot-only
  // client waits out a dry pool) and reclaimable mid-session, which is what the drain fields below describe.
  market?: MytaMarket;
  // Spot per client (myta-streams 2026-09-08): the GPU box this seat sits on is a SPOT instance that AWS is taking
  // back (`interrupt`, EC2 acts at `deadline`) or has flagged (`rebalance`). The seat is preserved until this page
  // moves or the box dies; `replacement` says whether on-demand capacity for THIS participant is ready, still
  // starting, or could not be arranged. Only ever present on a renew answer.
  draining?: boolean;
  drainingKind?: 'interrupt' | 'rebalance';
  interruptAt?: string;
  deadline?: string;
  replacement?: ILeaseReplacement;
  // The slot's newest event when it postdates this seat's grant (myta-streams 2026-09-09): the UE under the seat
  // was restarted and by whom (watchdog.*, ue.crash, ue.web-close, admin.*), never this page's own create/rebind.
  lastEvent?: ISeatEvent;
  // Seat lifetime (myta-streams 2026-09-10): when this VISIT ends and whether we are inside the warning
  // window. Only on renew answers, and only once a deadline is known.
  session?: ISessionLife;
}

export interface ISeatEvent {
  at: string;
  actor: string;
  reason?: string;
  // The systemd start timestamp (epoch seconds) of the UE unit this event belongs to — present on node-side
  // events only. It identifies the UE GENERATION, not the kind of restart (that is `actor`). A change under a
  // live seat means the process behind our peer is a NEW one, which is otherwise invisible: the library
  // re-subscribes to the same streamerId by itself and the picture simply comes back.
  unitEpoch?: number;
}

export interface ILeaseReplacement {
  status: 'ready' | 'pending' | 'none';
  nodeId?: string;
  phase?: string;
  buildUri?: string;
}

// A renew answered 409 node_gone: the seat was released server-side because the box is no longer running.
// `cause` names why (myta-streams 2026-09-09): spot-interrupt | node-stopped | node-terminated | node-absent | off-policy.
export interface ILeaseGone {
  gone: true;
  cause?: string;
  hint?: string;
}

export const isLeaseGone = (v: unknown): v is ILeaseGone =>
  typeof v === 'object' &&
  v !== null &&
  (v as { gone?: unknown }).gone === true;

// A renew answered 404 lease_not_current: the seat is not ours any more. As definitive as 409 — the difference
// is only that the machine is alive and the SEAT moved on, so the node-gone wording must not be reused.
// `cause` (myta-streams 2026-09-09): expired | released | superseded | evicted | node-gone | off-policy |
// spot-interrupt | operator | unknown, resolved from the actor that actually freed THIS lease; `lastEvent` is
// that seat-ending event and `slotEvent` the slot's newer, unrelated event (e.g. a restart that followed).
// The stream can still be running when this arrives — UE keeps its streamer up and the library re-subscribes
// on its own — so this answer, not the picture, is what says whether we still hold a seat.
// The seat-lifetime answer (403 session_ended, myta-streams api/seat-policy.ts, 2026-09-10). The ONE
// refusal that is not a reseat: every other definitive answer here (409 node_gone, 404 lease_not_current)
// means "the machine or the row moved, take a new lease" — this one means the VISIT is over, by the
// session cap, by inactivity, or because an operator kicked the user. Re-leasing on it would defeat the
// rule, so the provider must stop the renew loop and send the user back to the events page.
export interface ISessionEnded {
  ended: true;
  cause: 'max-session' | 'idle' | 'hidden' | string;
  why?: string;
  hint?: string;
}

export const isSessionEnded = (v: unknown): v is ISessionEnded =>
  typeof v === 'object' &&
  v !== null &&
  (v as { ended?: unknown }).ended === true;

// The countdown a renew carries once a deadline is in sight (`warn` inside `warnSec` of it). The hub
// renders it and lets a human extend the visit the only honest way: by doing something, which moves
// `lastInputAt` on the next renew.
export interface ISessionLife {
  endsAt: string;
  remainingSec?: number;
  warn?: boolean;
  warnCause?: string;
}

export interface ILeaseRefused {
  refused: true;
  error: string;
  cause?: string;
  lastEvent?: ISeatEvent;
  slotEvent?: ISeatEvent;
  hint?: string;
}

export const isLeaseRefused = (v: unknown): v is ILeaseRefused =>
  typeof v === 'object' &&
  v !== null &&
  (v as { refused?: unknown }).refused === true;

// The closed vocabulary the lease API accepts on DELETE `?reason=` → the session row's `endActor`
// (`lease.release-<reason>`); anything else is the plain release, so free text is never sent.
export type MytaReleaseReason =
  | 'spot-handoff'
  | 'give-up'
  | 'never-framed'
  | 'exit'
  // The AFK rule (2026-09-11): the page asked whether the user was still there and nobody answered. Its
  // own member rather than `exit`, which means the user deliberately left — the difference is the whole
  // point for anything reading session records, and an unanswered seat is a cost signal, not a departure.
  | 'afk';

export interface IAcquireLeaseProps {
  eventId?: string;
  userId?: string;
  buildUri?: string;
  /** This tab's seat-attempt counter, stamped on the seat and the session row (1-based). */
  attempt?: number;
}

export interface ILeasePhase {
  phase?: string;
  etaSec?: number;
  retryAfterSec?: number;
  provisioning?: boolean;
  idleFree?: number;
  hint?: string;
  // The market of the box this wait is ON, when there is one — absent on a bare `capacity_wait`, where no box
  // has been chosen yet. The server omits it rather than defaulting to `od`, so a missing field is "unknown",
  // never "ordinary".
  market?: MytaMarket;
}

export class NoFreeSlotError extends Error {
  details: ILeasePhase;

  constructor(details: ILeasePhase = {}) {
    super('no_free_slot');
    this.name = 'NoFreeSlotError';
    this.details = details;
  }
}

const storedLeaseKey = (eventId: string) => `myta:lease:${eventId}`;

export interface IStoredLease extends IMytaLease {
  eventId: string;
  storedAt: number;
}

export default class MytaStreamsService {
  static getLeaseApiUrl(): string {
    return (env.REACT_APP_MYTA_STREAMS_LEASE_API_URL || '')
      .trim()
      .replace(/\/+$/, '');
  }

  static getLeaseApiToken(): string {
    return (env.REACT_APP_MYTA_STREAMS_LEASE_API_TOKEN || '').trim();
  }

  static isLeaseApiConfigured(): boolean {
    return !!(this.getLeaseApiUrl() && this.getLeaseApiToken());
  }

  static isNativePixelStreamingEnabled(): boolean {
    return (
      (env.REACT_APP_FEATURE_NATIVE_PIXEL_STREAMING || '').trim() === 'true' &&
      this.isLeaseApiConfigured()
    );
  }

  private static headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.getLeaseApiToken()}`,
      'content-type': 'application/json',
    };
  }

  static async acquireLease(props: IAcquireLeaseProps): Promise<IMytaLease> {
    const startedAt = performance.now();
    const response = await fetch(`${this.getLeaseApiUrl()}/v1/ec2/leases`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(props),
    });
    console.log(
      `MYTA_TIMING lease_post status=${response.status} serverMs=${Math.round(
        performance.now() - startedAt,
      )}`,
    );

    if (response.status === 409) {
      const details: ILeasePhase = await response.json().catch(() => ({}));
      throw new NoFreeSlotError(details);
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Lease request failed: ${response.status} ${body}`);
    }

    return response.json();
  }

  // Reload survival: the lease is stashed per-tab (sessionStorage) so an F5 rebinds to the SAME slot
  // instead of releasing it — a release sends the slot through a restart cycle and the returning user
  // pays 60-90 s (and the ladder may even start a whole node). Tab close never releases either; the
  // server-side lease TTL (~120 s) reclaims abandoned seats — that is its designed job.
  static saveStoredLease(eventId: string, lease: IMytaLease): void {
    try {
      const stored: IStoredLease = { ...lease, eventId, storedAt: Date.now() };
      sessionStorage.setItem(storedLeaseKey(eventId), JSON.stringify(stored));
    } catch {
      /* storage unavailable — rebind is best-effort */
    }
  }

  static loadStoredLease(eventId: string): IStoredLease | null {
    try {
      const raw = sessionStorage.getItem(storedLeaseKey(eventId));
      if (!raw) {
        return null;
      }
      const stored: IStoredLease = JSON.parse(raw);
      if (!stored.leaseId || stored.eventId !== eventId) {
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }

  static clearStoredLease(eventId: string): void {
    try {
      sessionStorage.removeItem(storedLeaseKey(eventId));
    } catch {
      /* ignore */
    }
  }

  // `reason` names WHY a live seat is let go (spot handoff, watchdog give-up, never framed, user exit), so the
  // server's session record tells a happy exit from a seat handed back — it never guesses.
  static async releaseLease(
    leaseId: string,
    options: { keepalive?: boolean; reason?: MytaReleaseReason } = {},
  ): Promise<void> {
    try {
      const query = options.reason
        ? `?reason=${encodeURIComponent(options.reason)}`
        : '';
      await fetch(`${this.getLeaseApiUrl()}/v1/ec2/leases/${leaseId}${query}`, {
        method: 'DELETE',
        headers: this.headers(),
        keepalive: options.keepalive,
      });
    } catch (e) {
      console.warn(`Failed to release myta lease ${leaseId}`, e);
    }
  }

  // `null` = the lease is definitively gone (server answered non-2xx); `'network_error'` = the
  // request itself failed and the lease may still be alive — callers deciding whether to DISCARD a
  // stored lease must only do so on the definitive answer.
  // `rebind` marks the renew that a page load makes to claim a seat it already owned, as opposed to the
  // periodic keep-alive. The server needs the distinction: only on a rebind does it have to decide what to do
  // with the UE the disconnect left behind (restart it, or hand the seat back if a warm slot is free).
  // `ILeaseGone` (409 node_gone) is the third definitive answer: the seat is already released server-side because
  // the node is gone — `cause` says why — and the caller must take a fresh lease at once. `ILeaseRefused`
  // (404 lease_not_current) is the fourth: the seat is not ours any more and `lastEvent` names who freed it.
  static async renewLease(
    leaseId: string,
    opts: { rebind?: boolean; lastInputAt?: string; hidden?: boolean } = {},
  ): Promise<
    | IMytaLease
    | null
    | 'network_error'
    | ILeaseGone
    | ILeaseRefused
    | ISessionEnded
  > {
    try {
      const response = await fetch(
        `${this.getLeaseApiUrl()}/v1/ec2/leases/${leaseId}/renew`,
        {
          method: 'POST',
          headers: { ...this.headers(), 'content-type': 'application/json' },
          // The activity report (seat lifetime): `lastInputAt` is when this page last saw a real human
          // gesture, `hidden` whether the tab is backgrounded right now. Both optional by contract — a
          // seat that reports neither is never idle-ended, only capped.
          body: JSON.stringify({
            rebind: opts.rebind === true,
            ...(opts.lastInputAt ? { lastInputAt: opts.lastInputAt } : {}),
            ...(opts.hidden ? { hidden: true } : {}),
          }),
        },
      );

      if (!response.ok) {
        if (response.status === 403) {
          const body = (await response.json().catch(() => ({}))) as {
            error?: string;
            cause?: string;
            why?: string;
            hint?: string;
          };
          if (body.error === 'session_ended') {
            return {
              ended: true,
              cause: body.cause ?? 'max-session',
              why: body.why,
              hint: body.hint,
            };
          }
          return null;
        }
        if (response.status === 409) {
          const body = (await response.json().catch(() => ({}))) as {
            error?: string;
            cause?: string;
            hint?: string;
          };
          if (body.error === 'node_gone') {
            return { gone: true, cause: body.cause, hint: body.hint };
          }
        }
        if (response.status === 404) {
          const body = (await response.json().catch(() => ({}))) as {
            error?: string;
            cause?: string;
            lastEvent?: ISeatEvent;
            slotEvent?: ISeatEvent;
            hint?: string;
          };
          if (body.error === 'lease_not_current') {
            return {
              refused: true,
              error: body.error,
              cause: body.cause,
              lastEvent: body.lastEvent,
              slotEvent: body.slotEvent,
              hint: body.hint,
            };
          }
        }
        return null;
      }

      return await response.json();
    } catch (e) {
      console.warn(`Failed to renew myta lease ${leaseId}`, e);
      return 'network_error';
    }
  }
}
